import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { useRecoilState } from 'recoil';
import { Modal, Button, connectModal } from '@gsa/afp-component-library';
import OverlaySpinner from '../../../components/overlay-spinner';
import { APPROVE_REJECT_MODEL } from '../queries.gql';
import { makeModelStatusMessageAtom } from '../atoms/make-model-atoms';
import CommentSection from './make-model-modal-comment-section';
import {
  getMakeModelSuccessMessage,
  getMakeModelErrorMessage,
} from '../components/make-model-approval-helper';
import MakeModelSummary from '../components/make-model-summary';
import { MakeModelApprovalStatus } from '../constants';

const ModelApproveRejectModal = ({
  data,
  action,
  isOpen,
  onClose,
  refetchData,
}) => {
  const { make, modelCode, modelName, nhtsaModelCode } = data;
  const [, setToaster] = useRecoilState(makeModelStatusMessageAtom);
  const [commentBody, setCommentBody] = useState('');

  const isApprove = action === 'Approve';
  const modalTitle = `${isApprove ? 'Approve' : 'Reject'} make and model `;

  const [approveRejectModel, { loading }] = useMutation(APPROVE_REJECT_MODEL, {
    variables: {
      modelCode,
      fleetStatus:
        action === 'Approve'
          ? MakeModelApprovalStatus.Approved
          : MakeModelApprovalStatus.Rejected,
      comment: commentBody || '',
    },
    onCompleted: () => {
      onClose();
      setToaster({
        type: 'success',
        message: getMakeModelSuccessMessage(action, make.makeName, modelName),
      });
      refetchData();
    },
    onError: (err) => {
      onClose();
      setToaster({
        type: 'error',
        message: getMakeModelErrorMessage(
          err.message,
          action,
          make.makeName,
          modelName,
        ),
      });
    },
  });

  return (
    <Modal
      id="remove-standard-item-modal"
      variant="large"
      onClose={onClose}
      isOpen={isOpen}
      title={
        <h2 tabIndex="0" aria-label={modalTitle}>
          {modalTitle}
        </h2>
      }
      actions={
        <div className="flex-align-start flex-justify-start">
          <Button
            data-testid="approve-reject-modal-cancel-btn"
            variant="unstyled"
            onClick={onClose}
            className="padding-right-1"
            label="Cancel"
          />
          <Button
            variant={action === 'Approve' ? 'primary' : 'secondary'}
            onClick={approveRejectModel}
            label={action === 'Approve' ? 'Approve' : 'Reject'}
          />
        </div>
      }
    >
      <>
        {loading && <OverlaySpinner />}
        <div>
          <div className="margin-top-6 margin-bottom-8">
            {!isApprove && (
              <p className="margin-top-0">
                Rejecting this make and model will prevent any bid lines using
                this configuration from being submitted. Vehicle suppliers that
                proposed this make and model in their bid must propose a
                different combination to proceed.
              </p>
            )}
            <span>
              Are you sure you want to {isApprove ? 'approve' : 'reject'} this
              make and model?
            </span>
            <div className="margin-top-2">
              <MakeModelSummary
                make={make}
                modelName={modelName}
                modelCode={modelCode}
                nhtsaModelCode={nhtsaModelCode}
              />
            </div>
          </div>
          {!isApprove && (
            <CommentSection
              commentBody={commentBody}
              onCommentChange={setCommentBody}
            />
          )}
        </div>
      </>
    </Modal>
  );
};

ModelApproveRejectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.shape(
    PropTypes.objectOf({
      make: PropTypes.object.isRequired,
      makeName: PropTypes.string.isRequired,
      modelCode: PropTypes.string.isRequired,
      modelName: PropTypes.string.isRequired,
    }),
  ).isRequired,
  action: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchData: PropTypes.func.isRequired,
};

const ConnectedModelApprovalModal = connectModal(ModelApproveRejectModal);

export default ConnectedModelApprovalModal;
